import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, CheckCircleIcon, ChevronDownIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { config } from '../config/environment';
import AddressFields, { ApiCountry } from './AddressFields';
import ReactPhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { isValidPhoneNumber } from 'libphonenumber-js';

function Section({
  title, open, onToggle, required, children,
}: {
  title: string; open: boolean; onToggle: () => void; required?: boolean; children: React.ReactNode;
}) {
  return (
    <div className="border border-dark-border rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 bg-dark-hover hover:bg-dark-canvas transition-colors text-left"
      >
        <span className="text-sm font-semibold text-gray-200">
          {title}
          {required && <span className="ml-1 text-primary-400 text-xs">required</span>}
        </span>
        <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 py-5 space-y-4 border-t border-dark-border">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const MARITAL_OPTIONS = ['Single', 'Married', 'Divorced', 'Separated', 'Widowed', 'Prefer not to say'];
const INDUSTRY_OPTIONS = [
  'Technology', 'Healthcare', 'Education', 'Finance', 'Legal', 'Government',
  'Non-profit', 'Retail', 'Manufacturing', 'Media & Entertainment',
  'Real Estate', 'Consulting', 'Other',
];

export default function FillProfile() {
  const navigate = useNavigate();
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const clientId = userData.id || '';

  const [apiCountries, setApiCountries] = useState<ApiCountry[]>([]);
  useEffect(() => {
    fetch(`${config.apiBaseUrl}/countries`)
      .then(r => r.ok ? r.json() : [])
      .then(setApiCountries)
      .catch(() => {});
  }, []);

  const [open, setOpen] = useState({
    personal: true,
    family: false,
    emergency: false,
    occupation: false,
    additional: false,
  });

  const toggle = (section: keyof typeof open) =>
    setOpen(p => ({ ...p, [section]: !p[section] }));

  const [form, setForm] = useState({
    first_name: userData.first_name || '',
    last_name: userData.last_name || '',
    new_password: '',
    confirm_password: '',
    phone: '',
    date_of_birth: '',
    street_address: '',
    city: '',
    state_province: '',
    postal_code: '',
    country: '',
    marital_status: '',
    dependents: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    occupation: '',
    employer: '',
    industry: '',
    bio: '',
  });

  const [terms_accepted, setTermsAccepted] = useState(false);
  const [privacy_policy_accepted, setPrivacyAccepted] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: '' }));
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setForm(p => ({ ...p, country: e.target.value, state_province: '' }));
    if (errors.country) setErrors(p => ({ ...p, country: '' }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const required: Record<string, string> = {
      first_name: 'First name is required',
      last_name: 'Last name is required',
      new_password: 'Password is required',
      phone: 'Phone is required',
      date_of_birth: 'Date of birth is required',
      city: 'City is required',
      state_province: 'State / Province is required',
      postal_code: 'Postal code is required',
      country: 'Country is required',
    };
    if (required[name] && !value.trim()) {
      setErrors(p => ({ ...p, [name]: required[name] }));
    }
    if (name === 'confirm_password' && value !== form.new_password) {
      setErrors(p => ({ ...p, confirm_password: 'Passwords do not match' }));
    }
  };

  const passwordRules = [
    { rule: form.new_password.length >= 12,          label: 'At least 12 characters' },
    { rule: /[A-Z]/.test(form.new_password),         label: 'One uppercase letter' },
    { rule: /[a-z]/.test(form.new_password),         label: 'One lowercase letter' },
    { rule: /[0-9]/.test(form.new_password),         label: 'One number' },
    { rule: /[^A-Za-z0-9]/.test(form.new_password), label: 'One special character' },
  ];

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.first_name.trim())                e.first_name       = 'First name is required';
    if (!form.last_name.trim())                 e.last_name        = 'Last name is required';
    if (!form.new_password.trim())              e.new_password     = 'Password is required';
    else if (!passwordRules.every(r => r.rule)) e.new_password     = 'Password does not meet requirements';
    if (form.confirm_password !== form.new_password) e.confirm_password = 'Passwords do not match';
    if (!form.phone)                             e.phone            = 'Phone is required';
    else if (!isValidPhoneNumber(form.phone))    e.phone            = 'Enter a valid phone number';
    if (!form.date_of_birth.trim())             e.date_of_birth    = 'Date of birth is required';
    if (!form.city.trim())                      e.city             = 'City is required';
    if (!form.postal_code.trim())               e.postal_code      = 'Postal code is required';
    if (!form.state_province.trim())            e.state_province   = 'State / Province is required';
    if (!form.country.trim())                   e.country          = 'Country is required';
    if (!terms_accepted)                        e.terms_accepted   = 'You must accept the Terms of Use';
    if (!privacy_policy_accepted)               e.privacy_policy_accepted = 'You must accept the Privacy Policy';
    setErrors(e);
    // Open sections with errors
    if (e.first_name || e.last_name || e.new_password || e.confirm_password || e.phone || e.date_of_birth || e.street_address || e.city || e.postal_code || e.state_province || e.country) {
      setOpen(p => ({ ...p, personal: true }));
    }
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitted(true);
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const token = localStorage.getItem('authToken');
      const { confirm_password, ...formPayload } = form;
      const payload = { ...formPayload, terms_accepted, privacy_policy_accepted };
      const res = await fetch(`${config.apiBaseUrl}/clients/${clientId}/complete-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to save profile');
      const updated = { ...userData, must_reset_password: false };
      localStorage.setItem('userData', JSON.stringify(updated));
      const userType = localStorage.getItem('userType');
      navigate(userType === 'coach' ? '/dashboard/coach' : '/dashboard/client', { replace: true });
    } catch (err: any) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const base = 'w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-dark-canvas text-gray-200 placeholder-dark-muted';
  const field = (name: string) => `${base} ${errors[name] ? 'border-red-500' : 'border-dark-border'}`;

  return (
    <div className="min-h-screen bg-dark-canvas flex flex-col">
      <nav className="bg-dark-canvas border-b border-dark-border px-6 py-4 flex-shrink-0">
        <div className="max-w-2xl mx-auto flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-fuchsia-400 to-violet-500 rounded-lg flex items-center justify-center shadow-md">
            <SparklesIcon className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-white">CoachSight</span>
        </div>
      </nav>

      <div className="flex-1 flex items-start justify-center px-4 py-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-2xl">
          <div className="text-center mb-6">
            <div className="w-12 h-12 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircleIcon className="w-6 h-6 text-primary-400" />
            </div>
            <h1 className="text-2xl font-bold text-white">Welcome, {form.first_name || userData.first_name}!</h1>
            <p className="text-dark-muted mt-1 text-sm">Complete your profile to get started.</p>
          </div>

          {/* Identity — first/last name editable, email read-only */}
          <div className="bg-dark-card rounded-xl border border-dark-border p-5 mb-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Your Account</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">First Name *</label>
                <input
                  type="text"
                  name="first_name"
                  value={form.first_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={field('first_name')}
                  placeholder="First name"
                />
                {errors.first_name && <p className="text-red-400 text-xs mt-1">{errors.first_name}</p>}
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Last Name *</label>
                <input
                  type="text"
                  name="last_name"
                  value={form.last_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={field('last_name')}
                  placeholder="Last name"
                />
                {errors.last_name && <p className="text-red-400 text-xs mt-1">{errors.last_name}</p>}
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Email</p>
                <div className="px-3 py-2 rounded-lg bg-dark-hover border border-dark-border text-gray-400 text-sm truncate">{userData.email}</div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">

            {/* Personal Information */}
            <Section title="Personal Information" required open={open.personal} onToggle={() => toggle('personal')}>
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">Password *</label>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} name="new_password" value={form.new_password}
                    onChange={handleChange} onBlur={handleBlur}
                    className={field('new_password')} placeholder="Create a password" />
                  <button type="button" onClick={() => setShowPassword(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200">
                    {showPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                  </button>
                </div>
                {errors.new_password && <p className="text-red-400 text-xs mt-1">{errors.new_password}</p>}
                {form.new_password && (
                  <ul className="mt-2 space-y-1">
                    {passwordRules.map(({ rule, label }) => (
                      <li key={label} className={`flex items-center gap-1.5 text-xs ${rule ? 'text-green-400' : 'text-dark-muted'}`}>
                        <span>{rule ? '✓' : '○'}</span>{label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">Confirm Password *</label>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} name="confirm_password" value={form.confirm_password}
                    onChange={handleChange} onBlur={handleBlur}
                    className={field('confirm_password')} placeholder="Repeat your password" />
                  <button type="button" onClick={() => setShowPassword(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200">
                    {showPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                  </button>
                </div>
                {errors.confirm_password && <p className="text-red-400 text-xs mt-1">{errors.confirm_password}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">Phone *</label>
                <ReactPhoneInput
                  country="us"
                  value={form.phone.replace(/^\+/, '')}
                  onChange={val => { setForm(p => ({ ...p, phone: val ? `+${val}` : '' })); if (errors.phone) setErrors(p => ({ ...p, phone: '' })); }}
                  containerClass="rpi2-dark"
                  inputStyle={{ width: '100%', background: '#0d0b1e', border: `1px solid ${errors.phone ? '#ef4444' : '#2d2b45'}`, borderRadius: '0.5rem', color: '#e2e0f0', fontSize: '0.875rem', height: '38px' }}
                  buttonStyle={{ background: '#1a1830', border: `1px solid ${errors.phone ? '#ef4444' : '#2d2b45'}`, borderRight: 'none', borderRadius: '0.5rem 0 0 0.5rem' }}
                  dropdownStyle={{ background: '#0d0b1e', color: '#e2e0f0' }}
                  enableSearch
                />
                {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">Date of Birth *</label>
                <input type="date" name="date_of_birth" value={form.date_of_birth}
                  onChange={handleChange} onBlur={handleBlur} className={field('date_of_birth')} />
                {errors.date_of_birth && <p className="text-red-400 text-xs mt-1">{errors.date_of_birth}</p>}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-200 mb-2">Address *</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-200 mb-1">
                      Street Address <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      name="street_address"
                      value={form.street_address}
                      onChange={handleChange}
                      className={field('street_address')}
                      placeholder="Enter street address"
                    />
                  </div>
                  <AddressFields
                    country={form.country} state_province={form.state_province}
                    city={form.city} postal_code={form.postal_code}
                    errors={{ country: errors.country, state_province: errors.state_province, city: errors.city, postal_code: errors.postal_code }}
                    onChange={handleChange} onCountryChange={handleCountryChange} onBlur={handleBlur}
                    countryOptions={apiCountries.length > 0 ? apiCountries : undefined}
                  />
                </div>
              </div>
            </Section>

            {/* Family Information */}
            <Section title="Family Information" open={open.family} onToggle={() => toggle('family')}>
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">Marital Status</label>
                <select name="marital_status" value={form.marital_status} onChange={handleChange}
                  className={`${base} border-dark-border`}>
                  <option value="">Select…</option>
                  {MARITAL_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">Number of Dependents</label>
                <input type="number" name="dependents" value={form.dependents} min={0}
                  onChange={handleChange} className={`${base} border-dark-border`} placeholder="0" />
              </div>
            </Section>

            {/* Emergency Contact */}
            <Section title="Emergency Contact" open={open.emergency} onToggle={() => toggle('emergency')}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-1">Name</label>
                  <input type="text" name="emergency_contact_name" value={form.emergency_contact_name}
                    onChange={handleChange} className={`${base} border-dark-border`} placeholder="Full name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-1">Phone</label>
                  <ReactPhoneInput
                    country="us"
                    value={form.emergency_contact_phone.replace(/^\+/, '')}
                    onChange={(val: string) => setForm(p => ({ ...p, emergency_contact_phone: val ? `+${val}` : '' }))}
                    containerClass="rpi2-dark"
                    inputStyle={{ width: '100%', background: '#0d0b1e', border: '1px solid #2d2b45', borderRadius: '0.5rem', color: '#e2e0f0', fontSize: '0.875rem', height: '38px' }}
                    buttonStyle={{ background: '#1a1830', border: '1px solid #2d2b45', borderRight: 'none', borderRadius: '0.5rem 0 0 0.5rem' }}
                    dropdownStyle={{ background: '#0d0b1e', color: '#e2e0f0' }}
                    enableSearch
                  />
                </div>
              </div>
            </Section>

            {/* Occupation */}
            <Section title="Occupation" open={open.occupation} onToggle={() => toggle('occupation')}>
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">Job Title</label>
                <input type="text" name="occupation" value={form.occupation}
                  onChange={handleChange} className={`${base} border-dark-border`} placeholder="e.g. Product Manager" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">Employer</label>
                <input type="text" name="employer" value={form.employer}
                  onChange={handleChange} className={`${base} border-dark-border`} placeholder="Company name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">Industry</label>
                <select name="industry" value={form.industry} onChange={handleChange}
                  className={`${base} border-dark-border`}>
                  <option value="">Select…</option>
                  {INDUSTRY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </Section>

            {/* Additional Info */}
            <Section title="Additional Info" open={open.additional} onToggle={() => toggle('additional')}>
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">About You</label>
                <textarea name="bio" value={form.bio} onChange={handleChange} rows={3}
                  className={`${base} border-dark-border resize-none`}
                  placeholder="Anything you'd like your coach to know…" />
              </div>
            </Section>

            {/* Legal acceptance */}
            <div className="space-y-3 pt-2">
              <div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={terms_accepted}
                    onChange={e => { setTermsAccepted(e.target.checked); if (errors.terms_accepted) setErrors(p => ({ ...p, terms_accepted: '' })); }}
                    className="mt-0.5 w-4 h-4 rounded border-dark-border accent-primary-600 flex-shrink-0"
                  />
                  <span className="text-sm text-gray-300">
                    I have read and agree to the{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:text-primary-300 underline">
                      Terms of Use
                    </a>
                    {' '}<span className="text-red-400">*</span>
                  </span>
                </label>
                {errors.terms_accepted && <p className="text-red-400 text-xs mt-1 ml-7">{errors.terms_accepted}</p>}
              </div>
              <div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privacy_policy_accepted}
                    onChange={e => { setPrivacyAccepted(e.target.checked); if (errors.privacy_policy_accepted) setErrors(p => ({ ...p, privacy_policy_accepted: '' })); }}
                    className="mt-0.5 w-4 h-4 rounded border-dark-border accent-primary-600 flex-shrink-0"
                  />
                  <span className="text-sm text-gray-300">
                    I have read and agree to the{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:text-primary-300 underline">
                      Privacy Policy
                    </a>
                    {' '}<span className="text-red-400">*</span>
                  </span>
                </label>
                {errors.privacy_policy_accepted && <p className="text-red-400 text-xs mt-1 ml-7">{errors.privacy_policy_accepted}</p>}
              </div>
            </div>

            {submitError && <p className="text-red-400 text-sm">{submitError}</p>}
            {formSubmitted && Object.keys(errors).length > 0 && (
              <p className="text-red-400 text-sm">
                {Object.keys(errors).length} required {Object.keys(errors).length === 1 ? 'field is' : 'fields are'} missing or invalid — please review above.
              </p>
            )}

            <button type="submit" disabled={submitting}
              className="w-full py-3 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg font-medium transition-colors">
              {submitting ? 'Saving…' : 'Complete Profile & Continue'}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
